import { useEffect, useState } from 'react';
import { Message } from 'semantic-ui-react';
import Loader from '../../../../loader';
import Reconciliations from '../../../reconciliations/reconciliations';
import { BranchTypes } from '../../../../../constnats/schemaConstants';
import {
    updateReviewSchema,
    updateReconciliationsSchema,
    type UpdateReviewType,
    type UpdateReconciliationsType,
} from '../../../../../schemas/appliedUpdate.schemas';
import { getReviewsByUpdateIds } from '../../../../../service/tableUpdates';
import type { DescriptorType } from '../../../../../types/descriptor.types';
import DescriptorUtils from '../../../../../utils/descriptor/descriptorUtils';
import SchemaRepo from '../../../../../utils/repository/schemaRepo';
import TableUpdatesRepo from '../../../../../utils/repository/tableUpdatesRepo';
import type { AppliedUpdateChangesResponseType } from '../../../../../utils/repository/tableUpdatesRepo/schemas';
import FinancialReportChanges from '../financialReportChanges';

type UpdateReconciliationDetails = {
    update: UpdateReconciliationsType;
    reviews: UpdateReviewType[];
    reportFieldsById: Record<string, DescriptorType>;
};

function AppliedUpdateChanges({ updateId }: { updateId: number | string }) {
    const [updateChanges, setUpdateChanges] = useState<AppliedUpdateChangesResponseType | null>(null);
    const [reconciliationDetails, setReconciliationDetails] = useState<UpdateReconciliationDetails | null>(null);
    const [changesFailed, setChangesFailed] = useState(false);
    const [reconciliationFailed, setReconciliationFailed] = useState(false);

    useEffect(() => {
        let active = true;
        setUpdateChanges(null);
        setReconciliationDetails(null);
        setChangesFailed(false);
        setReconciliationFailed(false);

        TableUpdatesRepo.getChanges(updateId, (response: AppliedUpdateChangesResponseType) => {
            if (active) {
                setUpdateChanges(response);
            }
        }, () => {
            if (active) {
                setChangesFailed(true);
            }
        });
        const updateReconciliationsPromise = new Promise<unknown>((resolve, reject) => {
            TableUpdatesRepo.get(updateId, resolve, reject);
        }).then((response) => updateReconciliationsSchema.parse(response));
        const updateReviewsPromise = getReviewsByUpdateIds([updateId])
            .then((response) => updateReviewSchema.array().parse(response));
        const reportFieldsPromise = updateReconciliationsPromise.then((update) => new Promise<Record<string, DescriptorType>>((resolve, reject) => {
            SchemaRepo.get(update.srcSchemaId, (schema: { descriptors: DescriptorType[] }) => {
                const flatDescriptorsMap = DescriptorUtils.flatDescriptorsMap as
                    (descriptors: DescriptorType[]) => Record<string, DescriptorType>;
                resolve(flatDescriptorsMap(schema.descriptors));
            }, reject);
        }));

        Promise.all([updateReconciliationsPromise, updateReviewsPromise, reportFieldsPromise]).then(
            ([update, reviews, reportFieldsById]) => {
                if (active) {
                    setReconciliationDetails({ update, reviews, reportFieldsById });
                }
            },
            () => {
                if (active) {
                    setReconciliationFailed(true);
                }
            },
        );

        return () => { active = false; };
    }, [updateId]);

    if (changesFailed) {
        return <Message error>Unable to load update changes.</Message>;
    }
    if (!updateChanges) {
        return <Loader />;
    }

    const reviewBranchesBySchemaId: Record<number, { branchType: string; reviewType: string }> = {};
    for (const review of reconciliationDetails?.reviews ?? []) {
        if (review.schemaId !== null) {
            reviewBranchesBySchemaId[review.schemaId] = { branchType: BranchTypes.REVIEW, reviewType: review.type };
        }
    }

    return (
        <>
            <FinancialReportChanges title="Changes in peer review phase" reportChanges={updateChanges.changesInPearReviewPhase} />
            <FinancialReportChanges title="Changes in QA review phase" reportChanges={updateChanges.changesInQaReviewPhase} />
            <FinancialReportChanges title="Applied Changes" reportChanges={updateChanges.appliedChanges} />
            {reconciliationFailed ? (
                <Message error>Unable to load reconciliation details.</Message>
            ) : reconciliationDetails ? (
                <Reconciliations
                    reconciliationEvents={[...reconciliationDetails.update.reconciliationEvents].sort((a, b) => a.schemaId - b.schemaId)}
                    descriptorsMap={reconciliationDetails.reportFieldsById}
                    schemasKnowledge={reviewBranchesBySchemaId}
                />
            ) : <Loader />}
        </>
    );
}

export default AppliedUpdateChanges;
